import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, Loader2, HardDrive, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "../../../lib/api";
import { useLang } from "../../../context/LangContext";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Progress } from "../../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";

const CHUNK = 4 * 1024 * 1024;

function UploadPane({ cfg, onCreated }) {
  const { t } = useLang();
  const input = useRef(null);
  const [pct, setPct] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    const send = async (id, off, attempt = 0) => {
      try {
        await api.post(`/dub/upload/${id}/chunk?offset=${off}`, file.slice(off, off + CHUNK),
          { headers: { "Content-Type": "application/octet-stream" }, timeout: 180000 });
      } catch (err) {
        if (attempt >= 5 || (err.response && err.response.status < 500)) throw err;
        await new Promise((r) => setTimeout(r, 3000 * (attempt + 1)));
        return send(id, off, attempt + 1);
      }
      return undefined;
    };
    try {
      const { data: jobs } = await api.get("/dub/jobs");
      let job = jobs.find((j) => j.status === "uploading" && j.filename === file.name && j.outputs?.size === file.size);
      let start = job?.outputs?.received || 0;
      if (job) toast.info(t(`A retomar o upload em ${Math.round((start / file.size) * 100)}%`, `Resuming upload at ${Math.round((start / file.size) * 100)}%`));
      else job = (await api.post("/dub/upload/init", { filename: file.name, size: file.size })).data;
      onCreated();
      for (let off = start; off < file.size;) {
        try {
          await send(job.id, off);
          off += CHUNK;
        } catch (err) {
          if (err.response?.status !== 409) throw err;
          off = (await api.get(`/dub/jobs/${job.id}`)).data.outputs?.received ?? off;
        }
        setPct(Math.round((Math.min(off, file.size) / file.size) * 100));
      }
      await api.post(`/dub/upload/${job.id}/complete`);
      toast.success(t("Upload concluído. A validar o vídeo…", "Upload complete. Validating video…"));
      onCreated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setPct(null); if (input.current) input.current.value = ""; }
  };

  return (
    <div className="rounded-xl border border-dashed border-white/15 p-8 text-center">
      <input ref={input} type="file" hidden data-testid="dub-file-input" accept={(cfg?.extensions || []).join(",")} onChange={(e) => upload(e.target.files?.[0])} />
      <Upload className="mx-auto h-8 w-8 text-primary" />
      <p className="mt-3 text-sm text-muted-foreground">MP4 · MKV · MOV · AVI · {t("até", "up to")} {cfg?.max_minutes ?? 15} min</p>
      {pct !== null ? (
        <div className="mx-auto mt-4 max-w-sm" data-testid="dub-upload-progress"><Progress value={pct} /><p className="mt-2 text-xs text-muted-foreground">{pct}%</p></div>
      ) : (
        <Button data-testid="dub-choose-file-btn" className="mt-4 gap-2" onClick={() => input.current?.click()}><Upload className="h-4 w-4" />{t("Escolher ficheiro", "Choose file")}</Button>
      )}
    </div>
  );
}

function UrlPane({ cfg, onCreated }) {
  const { t } = useLang();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/dub/import-url", { url }); setUrl(""); toast.success(t("A importar o vídeo…", "Importing video…")); onCreated(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input data-testid="dub-url-input" placeholder="https://www.youtube.com/watch?v=…" value={url} onChange={(e) => setUrl(e.target.value)} />
        <Button data-testid="dub-import-url-btn" className="gap-2" disabled={!url.trim() || busy} onClick={submit}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}{t("Importar", "Import")}</Button>
      </div>
      <p className="text-xs text-muted-foreground">{(cfg?.platforms || []).join(" · ")}</p>
    </div>
  );
}

function MyCloudPane({ onCreated }) {
  const { t } = useLang();
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/dub/mycloud/files").then((r) => setFiles(r.data)).catch((err) => { setFiles([]); toast.error(formatApiErrorDetail(err.response?.data?.detail)); });
  useEffect(() => { load(); }, []);
  const imp = async (f) => {
    setBusy(f.id);
    try { await api.post("/dub/import-mycloud", { file_id: f.id }); toast.success(t("A validar o vídeo…", "Validating video…")); onCreated(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  if (!files) return <p className="text-sm text-muted-foreground">{t("A carregar a pasta…", "Loading folder…")}</p>;
  return (
    <div className="space-y-2" data-testid="dub-mycloud-list">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{t("Vídeos na pasta Blog do My Cloud (MP4, MKV, MOV, AVI).", "Videos in the My Cloud Blog folder (MP4, MKV, MOV, AVI).")}</p>
        <Button size="sm" variant="ghost" className="gap-1.5" data-testid="dub-mycloud-refresh" onClick={load}><RefreshCw className="h-4 w-4" />{t("Atualizar", "Refresh")}</Button>
      </div>
      {files.length === 0 && <p className="text-sm text-muted-foreground" data-testid="dub-mycloud-empty">{t("Nenhum vídeo na pasta. Coloque lá um ficheiro e clique em Atualizar.", "No videos in the folder. Add one and click Refresh.")}</p>}
      {files.map((f) => (
        <div key={f.id} className="flex items-center gap-3 rounded-lg border border-white/10 p-2.5" data-testid={`dub-mycloud-file-${f.id}`}>
          <span className="mr-auto min-w-0 break-words text-sm">{f.name} <span className="text-xs text-muted-foreground">· {Math.round((f.size || 0) / 1e6)} MB</span></span>
          <Button size="sm" className="gap-1.5" disabled={busy === f.id} data-testid={`dub-mycloud-import-${f.id}`} onClick={() => imp(f)}>{busy === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDrive className="h-4 w-4" />}{t("Importar", "Import")}</Button>
        </div>
      ))}
    </div>
  );
}

export default function DubImport({ cfg, onCreated }) {
  const { t } = useLang();
  return (
    <div className="rounded-2xl border border-white/10 bg-card p-6" data-testid="dub-import">
      <h3 className="font-heading font-bold mb-4">{t("Importar vídeo", "Import video")}</h3>
      <Tabs defaultValue="upload">
        <TabsList className="mb-4">
          <TabsTrigger value="upload" data-testid="dub-tab-upload">{t("Upload local", "Local upload")}</TabsTrigger>
          <TabsTrigger value="url" data-testid="dub-tab-url">{t("Por URL", "From URL")}</TabsTrigger>
          {cfg?.mycloud && <TabsTrigger value="mycloud" data-testid="dub-tab-mycloud">{t("Pasta Blog (My Cloud)", "Blog folder (My Cloud)")}</TabsTrigger>}
        </TabsList>
        <TabsContent value="upload"><UploadPane cfg={cfg} onCreated={onCreated} /></TabsContent>
        <TabsContent value="url"><UrlPane cfg={cfg} onCreated={onCreated} /></TabsContent>
        {cfg?.mycloud && <TabsContent value="mycloud"><MyCloudPane onCreated={onCreated} /></TabsContent>}
      </Tabs>
    </div>
  );
}
